import { Link } from 'react-router-dom';
import { Heart, PauseCircle, Wallet, PhoneCall, ExternalLink } from 'lucide-react';

// Safer Play — DCMS Prize Draws & Competitions Code of Practice, clauses 1.6–1.8.
// Written to be warm, human, non-judgmental. If you're tempted to make this sound
// more legal or defensive, please don't — that is expressly not what the Code asks
// for. Read like a considerate friend, not a compliance disclaimer.

const SupportOrg = ({ name, href, blurb }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="block bg-[color:var(--pd-bg)] border border-pd rounded-2xl p-5 hover:border-[color:var(--pd-accent-yellow)] transition-colors"
    data-testid={`safer-support-${name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-display text-xl leading-tight">{name}</h3>
      <ExternalLink size={14} className="text-muted-sage mt-1 shrink-0" />
    </div>
    <p className="mt-3 text-sm text-muted-sage leading-relaxed">{blurb}</p>
    <div className="mt-3 font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Visit</div>
  </a>
);

const SaferPlayPage = () => (
  <div className="container-pd py-14 md:py-20 max-w-4xl" data-testid="safer-play-page">
    {/* Hero */}
    <div className="max-w-3xl">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Safer Play</div>
      <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">We&apos;ve got your back.</h1>
      <p className="mt-5 text-lg text-muted-sage leading-relaxed">
        Entering a prize draw should feel like a small, fun thing — a bit of hope with a good coffee, not a way to fix a
        rough month. If it ever starts to feel like the second, we want to make it easy to step back, and we want you to
        know exactly what tools we&apos;ve built for that. No shame. No small print.
      </p>
    </div>

    {/* What we've built for you */}
    <div className="mt-14 space-y-5">
      <div className="rounded-2xl border border-pd bg-surface p-6" data-testid="safer-spend-limits">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-[color:var(--pd-bg)] p-3 border border-pd">
            <Wallet size={20} className="text-[color:var(--pd-gold)]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl">Set your own limit &mdash; from £0 to £250.</h2>
            <p className="mt-3 text-sm text-muted-sage leading-relaxed">
              Every account has a spend limit dial. You choose a figure that feels right &mdash; as low as £0 (which quietly
              blocks any purchase until you decide otherwise), up to the platform ceiling of £250 per month. Lowering your
              limit takes effect the instant you save it. Raising it always waits 24 hours, so a decision to spend more
              can never happen in a hot moment. You&apos;re in charge. We just hold the line.
            </p>
            <Link to="/account" className="mt-4 inline-flex items-center gap-1 text-sm text-[color:var(--pd-accent-yellow)]" data-testid="safer-cta-set-limit">
              Set or change your limit →
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-pd bg-surface p-6" data-testid="safer-pause">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-[color:var(--pd-bg)] p-3 border border-pd">
            <PauseCircle size={20} className="text-[color:var(--pd-gold)]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl">Take a break — 24 hours, a week, or six months.</h2>
            <p className="mt-3 text-sm text-muted-sage leading-relaxed">
              A pause is not a punishment. Sometimes life just wants a quieter inbox and no temptation to click. Pick 24
              hours or 7 days for a short reset. Pick a formal 6-month suspension for a firmer line — one we cannot let
              you undo early, precisely because you asked us not to. During any break, we stop all marketing to you
              automatically. You&apos;ll never hear from us on the way in.
            </p>
            <Link to="/account" className="mt-4 inline-flex items-center gap-1 text-sm text-[color:var(--pd-accent-yellow)]" data-testid="safer-cta-pause">
              Manage a pause →
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-pd bg-surface p-6" data-testid="safer-monthly-cap">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-[color:var(--pd-bg)] p-3 border border-pd">
            <Heart size={20} className="text-[color:var(--pd-gold)]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl">£250 monthly cap, always on.</h2>
            <p className="mt-3 text-sm text-muted-sage leading-relaxed">
              Every account on The Padel Draw is capped at <span className="text-[color:var(--pd-text)]">£250 total spend per calendar month</span>, regardless of card type. It&rsquo;s enforced automatically &mdash; you never have to think about it, and no one can accidentally overspend here. This is stricter than what the DCMS Prize Draws &amp; Competitions Code requires, and it&rsquo;s a moral commitment we&rsquo;ve made from day one. Any lower personal limit you set applies within this ceiling.
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* If something else is going on */}
    <div className="mt-16">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">If something else is going on</div>
      <h2 className="font-display text-4xl mt-3">Free, confidential support.</h2>
      <p className="mt-4 text-muted-sage leading-relaxed max-w-3xl">
        Prize draws should be for fun, never a way to solve money problems and never a substitute for real help. If
        anything below is starting to describe your life — please talk to someone. Everyone on this list is free, kind,
        and won&apos;t judge you.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <SupportOrg
          name="Citizens Advice"
          href="https://www.citizensadvice.org.uk/"
          blurb="Free, independent, confidential advice on money, debt, benefits, housing, employment — you name it. A brilliant first stop when you&apos;re not sure who to talk to first."
        />
        <SupportOrg
          name="National Debtline (Money Advice Trust)"
          href="https://nationaldebtline.org/"
          blurb="Specialists in debt advice. If bills or credit-card balances are keeping you up at night, they will help you work through it — for free, over the phone or online."
        />
        <SupportOrg
          name="Samaritans"
          href="https://www.samaritans.org/"
          blurb="Whatever you&apos;re going through, you can call Samaritans free any time, from any phone, on 116 123. They&apos;re not just for crises — sometimes you just need someone who will actually listen."
        />
        <SupportOrg
          name="Mind"
          href="https://www.mind.org.uk/"
          blurb="The UK&apos;s largest mental health charity. Practical, kind information and support lines for anyone whose head is a hard place to be right now."
        />
      </div>

      <div className="mt-6 flex items-start gap-3 text-sm text-muted-sage max-w-3xl">
        <PhoneCall size={16} className="text-[color:var(--pd-gold)] mt-1 shrink-0" />
        <p>
          If a competition — ours or anyone else&apos;s — is starting to feel less like fun and more like a pull, please
          use one of the tools above. That&apos;s exactly what they&apos;re there for.
        </p>
      </div>
    </div>

    {/* Our commitment */}
    <div className="mt-16 rounded-2xl border border-[color:var(--pd-gold)]/50 bg-surface p-6 md:p-8" data-testid="safer-our-commitment">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Our side of the deal</div>
      <h2 className="font-display text-3xl mt-3">What we do quietly, in the background.</h2>
      <ul className="mt-5 space-y-3 text-sm text-muted-sage leading-relaxed">
        <li>• We look at patterns — repeatedly hitting a spend limit, rapid entries, rapid card changes — not to punish anyone, but so a real person on our team can check in when something looks off.</li>
        <li>• We never sell your data. Never trade it. Never use it to build a marketing profile of your habits.</li>
        <li>• If any of our systems ever tell us a payment shouldn&apos;t go through — a limit breach, a credit-card cap, a mismatched postal entry — we refund you in full and explain why. You will never be left in the dark.</li>
        <li>• Every competition we run happens on its stated draw date. We have no minimum ticket threshold. Lower sales mean better odds. There is no scenario in which we cancel a draw and keep your money.</li>
      </ul>
      <p className="mt-6 text-sm text-muted-sage">
        We adopted the DCMS Prize Draws &amp; Competitions Code of Practice in full because we think you deserve at least
        this much clarity, and probably more.
      </p>
      <div className="mt-6 flex flex-wrap gap-3 text-sm">
        <Link to="/terms" className="text-[color:var(--pd-accent-yellow)]">Read the full Terms &amp; Conditions →</Link>
        <Link to="/privacy" className="text-[color:var(--pd-accent-yellow)]">Read our Privacy Policy →</Link>
      </div>
    </div>

    <p className="mt-10 text-xs text-muted-sage">
      This page will change over time as we learn what our players actually need. If something we&apos;ve written here
      doesn&apos;t sound helpful, please tell us:{' '}
      <a href="tel:+441156470600" className="text-[color:var(--pd-accent-yellow)]">+44 (0)115 647 0600</a>{' '}or{' '}
      <a href="mailto:hello@thepadeldraw.co.uk" className="text-[color:var(--pd-accent-yellow)]">hello@thepadeldraw.co.uk</a>.
    </p>
  </div>
);

export default SaferPlayPage;
